import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { ModalProvider } from './context/ModalContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import SEOHead from './components/SEOHead';
import Home from './pages/Home';
import Cars from './pages/Cars';
import Destinations from './pages/Destinations';
import Book from './pages/Book';
import Reviews from './pages/Reviews';
// import Payment from './pages/Payment';
import Contact from './pages/Contact';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import Notifications from './pages/Notifications';
import NotFound from './pages/NotFound';
import Prices from './pages/Prices';
import PrivacyPolicy from './pages/PrivacyPolicy';
import api from './utils/api';
import { initFacebookPixel, trackPageView, removeFacebookPixel } from './utils/facebookPixel';
import { pushPageView, pushSessionStart } from './utils/dataLayer';
import { usePageEngagement } from './hooks/usePageEngagement';

const AppContent = () => {
  const location = useLocation();
  const validRoutes = ['/', '/cars', '/prices', '/destinations', '/book', '/reviews', '/contact', '/profile', '/notifications', '/policy', '/privacy-policy'];
  const hideNavFooter = ['/login', '/register', '/forgot-password', '/reset-password', '/admin'].includes(location.pathname) ||
    !validRoutes.includes(location.pathname);

  // Track if this is the initial page load (Facebook Pixel)
  const isInitialLoad = useRef(true);
  const currentPixelId = useRef(null);
  // GTM/GA4: session_start once per session
  useEffect(() => {
    pushSessionStart();
  }, []);
  // GTM/GA4: page_view on every route (SPA – no reload; we push explicitly)
  useEffect(() => {
    pushPageView({
      page_path: location.pathname,
      page_title: document.title,
      page_location: window.location.href,
    });
  }, [location.pathname]);

  // GTM/GA4: scroll depth (25/50/75) and time on page (10s/30s) per virtual page
  usePageEngagement();

  // Initialize Facebook Pixel on app load
  useEffect(() => {
    const initializePixel = async () => {
      try {
        const response = await api.get('/settings');
        const pixelId = response.data?.facebookPixelId;

        if (pixelId && pixelId.trim()) {
          currentPixelId.current = pixelId;
          await initFacebookPixel(pixelId);
        } else {
          // Remove pixel if ID is cleared
          if (currentPixelId.current) {
            removeFacebookPixel();
            currentPixelId.current = null;
          }
        }
      } catch (error) {
        console.error('[Facebook Pixel] Error fetching settings:', error);
      }
    };

    initializePixel();

    // Poll for settings changes every 5 minutes (optional, for dynamic updates)
    const pollInterval = setInterval(initializePixel, 5 * 60 * 1000);

    return () => clearInterval(pollInterval);
  }, []);

  // Facebook Pixel: track PageView on route changes (skip first load – init already does it)
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    trackPageView();
  }, [location.pathname]);

  return (
    <div className="app-wrapper">
      <SEOHead />
      <ScrollToTop />
      {!hideNavFooter && <Navbar />}
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/cars" element={<Cars />} />
          <Route path="/prices" element={<Prices />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/destinations" element={<Destinations />} />
          <Route path="/book" element={<Book />} />
          <Route path="/reviews" element={<Reviews />} />
          {/* <Route path="/payment" element={<Payment />} /> */}
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!hideNavFooter && <Footer />}
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <ModalProvider>
        <Router>
          <AppContent />
        </Router>
      </ModalProvider>
    </LanguageProvider>
  );
}

export default App;